import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../utils/apiUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import userDataService from "../../services/userDataService";
import { USER_TYPES } from "../../constants/userTypes";
import "./ChatDropdown.css";

const ChatDropdown = ({ isOpen, onClose, colors }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const userType = userDataService.getUserType();

  // Color schemes based on user type
  const getColorScheme = (userType) => {
    const type = userType?.toUpperCase();
    switch (type) {
      case "PET_GUARDIAN":
        return { primary: "#8548A8", hover: "#7A4397" };
      case "VETERINARIAN":
      case "VET":
        return { primary: "#00D4B3", hover: "#00C4A7" };
      case "SERVICE_PROVIDER":
      case "BUSINESS":
        return { primary: "#D65745", hover: "#C7493A" };
      default:
        return { primary: "#8548A8", hover: "#7A4397" };
    }
  };

  // Fetch chats from API
  const fetchChats = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const API_BASE_URL =
        import.meta.env.VITE_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${API_BASE_URL}/api/v1/conversations/chats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success" && result.data) {
        setChats(result.data);
      } else {
        setError("Failed to fetch chats");
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chats");
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch chats when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Reset chat badge count to 0 when dropdown opens
      localStorage.setItem('chat_badge_count', '0');
      const badgeUpdateEvent = new CustomEvent('chat-badge-update', {
        detail: {
          count: 0,
          timestamp: new Date().toISOString(),
        }
      });
      window.dispatchEvent(badgeUpdateEvent);

      fetchChats();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? "Just now" : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) {
      // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get display name for chat
  const getChatDisplayName = (chat) => {
    if (chat.title) {
      return chat.title;
    }
    if (chat.other_participant) {
      const first = chat.other_participant.first_name || ''
      const last = chat.other_participant.last_name || ''
      const name = `${first} ${last}`.trim()
      return name || 'Unknown User'
    }
    return "Unknown Chat";
  };

  // Get display image for chat
  const getChatDisplayImage = (chat) => {
    if (chat.other_participant?.profile_image_url) {
      return chat.other_participant.profile_image_url;
    }
    return getUserPlaceholderImage(chat.other_participant || chat);
  };

  // Handle chat click
  const handleChatClick = (chatId, receiverId, chatType) => {
    if (chatType === "medical") {
      if (userType == USER_TYPES.VETERINARIAN) {
        navigate(`/medical/clinic/chat/${receiverId}`);
      } else {
        navigate(`/chat/${receiverId}/medical`);
      }
    } else {
      navigate(`/chat/${receiverId}/social`);
    }
    onClose();
  };

  // Handle view all chats
  const handleViewAllChats = () => {
    navigate("/chat");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="chat-dropdown"
      style={{
        '--chat-dropdown-primary-color': colors.primary || "#2a8ad2",
      }}
    >
      {/* Header */}
      <div className="chat-dropdown-header">
        <h3>{t("navbar.Messages")}</h3>
        <button
          onClick={handleViewAllChats}
          className="chat-dropdown-view-all-btn"
        >
          {t("navbar.View_All_Messages")}
        </button>
      </div>

      {/* Content */}
      <div className="chat-dropdown-content">
        {isLoading ? (
          <div className="chat-dropdown-loading">
            <div className="chat-dropdown-spinner" />
            {t("navbar.Loading_Chats")}
          </div>
        ) : error ? (
          <div className="chat-dropdown-error">
            {error}
          </div>
        ) : chats.length === 0 ? (
          <div className="chat-dropdown-empty">
            <div className="chat-dropdown-empty-icon">💬</div>
            <p className="chat-dropdown-empty-text">
              {t("navbar.No_Messages")}
            </p>
            <p className="chat-dropdown-empty-subtext">
              {t("navbar.Start_Conversation")}
            </p>
          </div>
        ) : (
          chats.map((chat) => (
            <div
              key={chat.id}
              onClick={() =>
                handleChatClick(chat.id, chat.other_participant.id, chat.type)
              }
              className={`chat-dropdown-item ${chat.type === "medical" ? "medical" : ""}`}
            >
              {/* Chat Avatar */}
              <div className="chat-dropdown-avatar-container">
                <img
                  src={getChatDisplayImage(chat)}
                  alt={getChatDisplayName(chat)}
                  className="chat-dropdown-avatar"
                  style={{
                    border: `2px solid ${getColorScheme(chat.other_participant?.user_type).primary}`,
                    borderRadius: "50%"
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(chat.other_participant || chat);
                  }}
                />
                {/* Online indicator */}
                {chat.is_online && (
                  <div className="chat-dropdown-online-indicator" />
                )}
              </div>

              {/* Chat Info */}
              <div className="chat-dropdown-info">
                <div className="chat-dropdown-info-header">
                  <h4 className="chat-dropdown-chat-name">
                    {getChatDisplayName(chat)}
                  </h4>
                  {chat.latest_message && (
                    <span className="chat-dropdown-time">
                      {formatTime(chat.latest_message.created_at)}
                    </span>
                  )}
                </div>

                {/* Latest Message */}
                <div className="chat-dropdown-message-container">
                  <p className="chat-dropdown-latest-message">
                    {chat.latest_message ? (
                      <>
                        {chat.latest_message.user?.first_name && (
                          <span className="chat-dropdown-message-sender">
                            {chat.latest_message.user.first_name}:{" "}
                          </span>
                        )}
                        {chat.latest_message.content}
                      </>
                    ) : (
                      t("navbar.No_Messages")
                    )}
                  </p>

                  {/* Unread count */}
                  {chat.unread_count > 0 && (
                    <div className="chat-dropdown-unread-badge">
                      {chat.unread_count > 99 ? "99+" : chat.unread_count}
                    </div>
                  )}
                </div>

                {/* Status and type indicator */}
                <div className="chat-dropdown-type-indicator">
                  {!chat.is_online && chat.last_seen_at && (
                    <span className="chat-dropdown-status">
                      {chat.last_seen_at}
                    </span>
                  )}
                  {chat.is_online && (
                    <span className="chat-dropdown-status online">
                      Online
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {chats.length > 0 && (
        <div className="chat-dropdown-footer">
          <button
            onClick={handleViewAllChats}
            className="chat-dropdown-footer-btn"
          >
            {t("navbar.View_All_Messages")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatDropdown;
